import React, { useEffect, useRef, useState } from 'react';

import { HI_TYPE_ICONS, TYPE_INVOICE, hiTypeLabel } from '../../../constants/abdmHiTypes';
import {
  CARD, INVOICE, NOT_APPROVED, ROW, ROWS, fileCount, slotName,
} from '../../../constants/hipRecordsCopy';
import {
  SLOT_STATE, SLOT_SOURCE, isBlocked, isEmptyRecord, isNotApproved,
} from '../../../constants/hipRecordEnums';
import { INVOICE_STATUS, PREFILL_KIND } from '../../../constants/hipInvoiceEnums';
import { formatDateTimeISTWithYearNoSeconds } from '../../../utils/formatIST';
import { formatPaise } from '../../../utils/money';
import { pausedUntilMs, pausedWhenIST, pausedSentenceOf } from './slotTime';

/**
 * HipRecordRows - THE checklist of an encounter card (redesign v2, OR-65/67/70/73): one row per slot the card
 * holds, in the order given (the server sends the fixed M3 order; several Diagnostic Reports / Invoices get a
 * row each, keyed by `slotKey`), then one row per unsaved invoice suggestion.
 * Pure presentation: no fetching, no selection state of its own. The card owns the ticks.
 *
 * Props (FROZEN, R-F4 binds them):
 *   record      an EncounterRow (the card hands in its slots with applied writes on top)
 *   suggestions EncounterRow.invoice_suggestions (unsaved invoice starting points)
 *   ticked      Set<slotKey>: the rows whose tick box is on
 *   onToggle(slot)             a tick box was clicked
 *   onView(slot, index)        "View" (the preview has Download + Replace)
 *   onOpenInvoice(slot)        "Open bill" (the invoice form; it is where an invoice is linked)
 *   onDeleteInvoice(slot)      the trash icon of a DRAFT invoice
 *   onOpenSuggestion(g)        "Fill in bill →"
 *   disabled                   a request is running: the tick boxes and the trash are off
 *   focusSlotKey / onFocused   (OR-82) after an inline Add file / Replace: show the row of this slot (expanding "Show n more"
 *                              if it sits behind it) and focus its "View" link, then call onFocused() once
 *
 * Row = [tick box | status dot] + name + state words + ONE text action. The tick box shows only on a row the
 * card's "Link N records" button can link (`isTickable`); every other row gets a dot instead. State is the dot's
 * glyph AND words, never colour alone. State comes from the server-derived `slot.state` only (I8).
 * An Invoice has no tick (OR-70): it links from its own form. A prescription the doctor has not approved
 * (OR-55) is a greyed row with the reason, and nothing to tick or open. So is an OP Consultation that would be an empty
 * record (OR-79, blocked_reason RX_REQUIRED): greyed, "Record is empty. It can't be linked to ABHA.", no View - except on a
 * visit with no doctor, where the card never hands this component that slot at all (OR-87: no row; see HipEncounterCard).
 * More than ROW_CAP rows: the first ROW_CAP, then "Show n more" / "Show less".
 *
 * Also exported for the preview modal: `sourceLineOf`, `replaceLockReason`, `ReplacePencil`, `TAG`.
 */

export const ROW_CAP = 5; // the card shows "Select all" past it (rows beyond it are hidden)

const when = (iso) => formatDateTimeISTWithYearNoSeconds(iso) || '—';

// Where the slot's file came from (rows 23/24/29). Used by the preview modal's header.
export function sourceLineOf(slot) {
  if (slot.state === SLOT_STATE.LINKED) return ROWS.linkedCopy(when(slot.linkedAt));
  if (slot.source === SLOT_SOURCE.STAFF) {
    return ROWS.uploaded(when(slot.sourceAt), slot.files.length === 1 ? slot.files[0].fileName : null);
  }
  return ROWS.savedInApp(when(slot.sourceAt));
}

// Why the pencil is locked (rows 26 / 27 / 65); undefined while the slot state allows Replace.
export function replaceLockReason(slot) {
  if (slot.state === SLOT_STATE.LINKED) return ROWS.replaceLockedLinked;
  if (slot.state === SLOT_STATE.LINKING) return ROWS.replaceLockedLinking;
  if (slot.state === SLOT_STATE.PAUSED) return CARD.pausedReason(pausedWhenIST(pausedUntilMs(slot)), slot.pauseReason);
  return undefined;
}

// The ONE rule for "this row has a tick box" (INV-S): the server says it can be linked, it is not an Invoice
// (linked from its own form, OR-70) and it is not blocked (a prescription not approved yet, OR-55; an empty OP
// Consultation, OR-79). The card derives its ticked set from the same predicate, so the box and the button's N never disagree.
export const isTickable = (slot) => slot.canLink && !slot.invoice && !isBlocked(slot);

// Solid fills in both modes (CLAUDE.md contrast rule).
const ROW_CLS = 'flex flex-wrap items-center gap-x-2.5 gap-y-0.5 border-b border-slate-300 py-2 last:border-b-0 dark:border-slate-600';
const BTN = 'rounded-md border px-2.5 py-1 text-xs font-semibold whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60';
const BTN_TEAL = `${BTN} border-teal-500 bg-teal-50 text-teal-700 hover:bg-teal-100 dark:border-teal-400 dark:bg-teal-900 dark:text-teal-300 dark:hover:bg-teal-800`;
const BTN_MORE = 'mt-1.5 w-full rounded-md border border-slate-400 bg-slate-100 px-3 py-1.5 text-center text-xs font-semibold text-slate-800 hover:bg-slate-200 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700';
// "View" / "Open bill": a plain text link on the card's solid surface, tinted in both modes.
const LINK = 'p-0 text-xs font-semibold text-teal-700 underline hover:text-teal-900 dark:text-teal-300 dark:hover:text-teal-100';
const PENCIL = 'inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm leading-none';
const PENCIL_ON = `${PENCIL} border-slate-400 bg-slate-100 text-slate-800 hover:bg-slate-200 dark:border-slate-400 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-600`;
const PENCIL_LOCKED = `${PENCIL} cursor-help border-slate-300 bg-slate-100 text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400`;
const TRASH = `${PENCIL} border-red-600 bg-white text-red-800 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-400 dark:bg-slate-800 dark:text-red-300 dark:hover:bg-slate-600`;
export const TAG = 'ml-1.5 rounded-full border border-sky-700 bg-sky-100 px-1.5 py-px align-[1px] text-[10.5px] font-bold text-sky-700 dark:border-sky-300 dark:bg-sky-900 dark:text-sky-200';

// The pencil (OR-10): icon only. Locked = aria-disabled (not `disabled`) so a tap still lands and
// onLockedTap can show why (touch has no hover). Used by the preview modal's footer.
// `lockReason` = a reason from the card on top of the slot's own (an inline panel is open, one per card, OR-82).
export function ReplacePencil({
  slot, onReplace, onLockedTap, lockReason: cardLock,
}) {
  const lockReason = replaceLockReason(slot) || cardLock;
  const canReplace = !lockReason && slot.canReplace;
  const tip = canReplace ? ROWS.replace : (lockReason || ROWS.replace);
  return (
    <button
      type="button"
      className={canReplace ? PENCIL_ON : PENCIL_LOCKED}
      aria-label={tip}
      title={tip}
      aria-disabled={canReplace ? undefined : 'true'}
      onClick={canReplace ? () => onReplace(slot) : onLockedTap}
    >
      <span aria-hidden="true">✎</span>
    </button>
  );
}

// ── the status dot: a glyph, so the state never rests on colour alone ─────────────────
const DOT_BASE = 'inline-grid h-5 w-5 place-items-center rounded-full border-2 text-[11px] font-extrabold leading-none';
const DOT = {
  done: { glyph: '✓', cls: 'border-green-700 bg-green-100 text-green-800 dark:border-green-400 dark:bg-green-900 dark:text-green-200' },
  todo: { glyph: '○', cls: 'border-slate-500 bg-white text-slate-700 dark:border-slate-400 dark:bg-slate-800 dark:text-slate-200' },
  fail: { glyph: '!', cls: 'border-red-600 bg-red-100 text-red-800 dark:border-red-400 dark:bg-red-900 dark:text-red-200' },
  paused: { glyph: '‖', cls: 'border-amber-600 bg-yellow-100 text-yellow-800 dark:border-amber-400 dark:bg-yellow-900 dark:text-yellow-200' },
  wait: { glyph: '…', cls: 'border-amber-600 bg-yellow-100 text-yellow-800 dark:border-amber-400 dark:bg-yellow-900 dark:text-yellow-200' },
  none: { glyph: '–', cls: 'border-slate-400 bg-slate-100 text-slate-600 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-300' },
};
const WORDS_TONE = {
  ok: 'text-green-800 dark:text-green-300',
  warn: 'text-yellow-800 dark:text-yellow-200',
  bad: 'text-red-800 dark:text-red-300',
  plain: 'text-slate-900 dark:text-slate-100',
  muted: 'text-slate-600 dark:text-slate-300',
};

// Dot + words + optional hint of a slot, from the server-derived `slot.state` only (I8). READY is "Not linked yet".
function stateOf(slot) {
  if (slot.state === SLOT_STATE.LINKED) return { dot: DOT.done, tone: 'ok', words: ROW.linked(formatDateTimeISTWithYearNoSeconds(slot.linkedAt)) };
  if (slot.state === SLOT_STATE.LINKING) return { dot: DOT.wait, tone: 'warn', words: ROW.linking };
  if (slot.state === SLOT_STATE.PAUSED) return { dot: DOT.paused, tone: 'warn', words: pausedSentenceOf(slot) };
  if (slot.state === SLOT_STATE.LINK_FAILED) return { dot: DOT.fail, tone: 'bad', words: ROW.failed, hint: ROW.failedHint };
  return { dot: DOT.todo, tone: 'plain', words: ROW.notLinkedYet };
}

// The shared row shell. Phone (390 px): dot | name | action on line 1, the state words on line 2 under the name.
function Row({
  label, lead, name, sub, words, tone, hint, action, muted, attrs,
}) {
  return (
    <li aria-label={label} className={ROW_CLS} {...attrs}>
      <span className="flex h-6 w-6 flex-none items-center justify-center">{lead}</span>
      <div className="min-w-0 flex-[1_1_140px]">
        <p className={`m-0 text-[13px] [overflow-wrap:anywhere] ${muted ? WORDS_TONE.muted : 'text-slate-900 dark:text-slate-100'}`}>{name}</p>
        {sub && <p className="m-0 mt-px text-[11.5px] text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">{sub}</p>}
      </div>
      <div className="order-last basis-full pl-[34px] sm:order-none sm:grow sm:basis-[170px] sm:pl-0">
        <p className={`m-0 text-[12.5px] font-bold ${WORDS_TONE[tone]}`}>{words}</p>
        {hint && <p className="m-0 text-[11.5px] text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">{hint}</p>}
      </div>
      {/* A fixed width from sm up (also on a row with no action) keeps the state words in one column. */}
      <div className={`flex-none items-center justify-end gap-1.5 sm:flex sm:min-w-[120px] ${action ? 'flex' : 'hidden'}`}>{action}</div>
    </li>
  );
}

const Dot = ({ dot }) => <span aria-hidden="true" className={`${DOT_BASE} ${dot.cls}`}>{dot.glyph}</span>;

function RecordRow({
  slot, ticked, onToggle, onView, disabled,
}) {
  const label = slotName(slot);
  const st = stateOf(slot);
  const linked = slot.state === SLOT_STATE.LINKED;
  const fileName = slot.source === SLOT_SOURCE.STAFF && slot.files.length === 1 ? slot.files[0].fileName : null;
  const sub = [fileName, slot.fileCount > 1 ? fileCount(slot.fileCount) : null].filter(Boolean).join(' · ');

  const lead = isTickable(slot) ? (
    <input
      type="checkbox" className="h-5 w-5 cursor-pointer accent-teal-600 disabled:cursor-not-allowed"
      checked={ticked.has(slot.slotKey)} disabled={disabled} aria-label={ROW.tick(label)} onChange={() => onToggle(slot)}
    />
  ) : <Dot dot={st.dot} />;

  return (
    <Row
      label={label} lead={lead} tone={st.tone} words={st.words} hint={st.hint} sub={sub || undefined}
      attrs={{
        'data-record-type': slot.recordType, 'data-slot-key': slot.slotKey, 'data-state': slot.state,
      }}
      name={(
        <>
          <span aria-hidden="true">{HI_TYPE_ICONS[slot.recordType]} </span>
          <b>{label}</b>
          {slot.replacedByStaff && <span className={TAG}>{ROWS.replacedByStaff}</span>}
        </>
      )}
      action={(
        <button type="button" className={LINK} title={linked ? ROWS.linkedCopyTip : undefined} onClick={() => onView(slot, 0)}>
          {slot.fileCount > 1 ? ROW.viewN(slot.fileCount) : ROW.view}
        </button>
      )}
    />
  );
}

// A prescription that is a draft / pending approval / rejected (OR-55): greyed, the reason in words, nothing to tick or open.
function NotApprovedRow({ slot }) {
  return (
    <Row
      label={slotName(slot)} lead={<Dot dot={DOT.none} />} muted tone="muted" words={ROW.notApproved} hint={NOT_APPROVED.reason}
      attrs={{
        'data-record-type': slot.recordType, 'data-slot-key': slot.slotKey, 'data-state': slot.state, 'data-blocked': slot.blockedReason,
      }}
      name={(
        <>
          <span aria-hidden="true" className="opacity-60">{HI_TYPE_ICONS[slot.recordType]} </span>
          <b>{hiTypeLabel(slot.recordType)}</b>
        </>
      )}
    />
  );
}

// An OP Consultation with no approved prescription and no staff file (OR-79): the record would be empty. Greyed, no
// tick, nothing to open; the OP Consultation pill's Add file is the way out.
function EmptyRecordRow({ slot }) {
  return (
    <Row
      label={slotName(slot)} lead={<Dot dot={DOT.none} />} muted tone="muted" words={ROW.emptyRecord}
      attrs={{
        'data-record-type': slot.recordType, 'data-slot-key': slot.slotKey, 'data-state': slot.state, 'data-blocked': slot.blockedReason,
      }}
      name={(
        <>
          <span aria-hidden="true" className="opacity-60">{HI_TYPE_ICONS[slot.recordType]} </span>
          <b>{hiTypeLabel(slot.recordType)}</b>
        </>
      )}
    />
  );
}

// One saved invoice (A1.2): no files, no tick (OR-70). "Open bill" opens its form, where it is linked; a DRAFT also
// has the trash icon (R12: it is the only place a draft is deleted). The amount never wraps inside itself.
function InvoiceRow({
  slot, onOpenInvoice, onDeleteInvoice, disabled,
}) {
  const { invoice } = slot;
  const st = stateOf(slot);
  const head = INVOICE.card.rowHead(invoice);
  const amount = formatPaise(invoice.totalGrossPaise);

  return (
    <Row
      label={`${head}${INVOICE.sep}${amount}`} lead={<Dot dot={st.dot} />} tone={st.tone} words={st.words}
      hint={st.hint || (slot.state === SLOT_STATE.READY ? INVOICE.card.row.filled : undefined)}
      attrs={{ 'data-record-type': slot.recordType, 'data-slot-key': slot.slotKey, 'data-state': slot.state }}
      name={(
        <>
          <span aria-hidden="true">{HI_TYPE_ICONS[slot.recordType]} </span>
          <b>{head}{INVOICE.sep}<span className="whitespace-nowrap">{amount}</span></b>
        </>
      )}
      action={(
        <>
          <button
            type="button" className={LINK} title={slot.state === SLOT_STATE.LINKED ? ROWS.linkedCopyTip : undefined}
            onClick={() => onOpenInvoice(slot)}
          >
            {INVOICE.card.row.open}
          </button>
          {invoice.status === INVOICE_STATUS.DRAFT && (
            <button
              type="button" className={TRASH} disabled={disabled}
              aria-label={INVOICE.card.deleteDraft} title={INVOICE.card.deleteDraft} onClick={() => onDeleteInvoice(slot)}
            >
              <span aria-hidden="true">🗑</span>
            </button>
          )}
        </>
      )}
    />
  );
}

const SUGGESTION_FROM = {
  [PREFILL_KIND.RECEIPT]: INVOICE.card.row.fromReceipt,
  [PREFILL_KIND.FINAL_BILL]: INVOICE.card.row.fromFinalBill,
};

// An unsaved starting point (a settled receipt / the final bill): not an invoice yet, so no state beyond "not filled in".
function SuggestionRow({ suggestion, onOpen }) {
  const name = slotName({ recordType: TYPE_INVOICE, label: suggestion.billNumber });
  return (
    <Row
      label={name} lead={<Dot dot={DOT.todo} />} tone="plain" words={INVOICE.card.row.notFilled} hint={SUGGESTION_FROM[suggestion.sourceKind]}
      name={(
        <>
          <span aria-hidden="true">{HI_TYPE_ICONS[TYPE_INVOICE]} </span>
          <b>{name}</b>
        </>
      )}
      action={<button type="button" className={BTN_TEAL} onClick={() => onOpen(suggestion)}>{INVOICE.card.row.fillIn}</button>}
    />
  );
}

const NO_SUGGESTIONS = [];

export default function HipRecordRows({
  record, suggestions = NO_SUGGESTIONS, ticked, onToggle, onView, onOpenInvoice, onDeleteInvoice, onOpenSuggestion, disabled = false,
  focusSlotKey = null, onFocused,
}) {
  const [showAll, setShowAll] = useState(false); // survives a refetch: the card keeps this component mounted
  const listRef = useRef(null);
  const slotIndex = focusSlotKey ? record.slots.findIndex((s) => s.slotKey === focusSlotKey) : -1;
  // OR-82: after an inline write, show the slot's row (expand "Show n more" if it hides it) and focus its "View" link.
  useEffect(() => {
    if (!focusSlotKey) return;
    if (slotIndex >= ROW_CAP && !showAll) { setShowAll(true); return; } // this effect runs again once the rows are drawn
    const row = Array.from(listRef.current ? listRef.current.querySelectorAll('[data-slot-key]') : [])
      .find((el) => el.getAttribute('data-slot-key') === focusSlotKey);
    const view = row && row.querySelector('button');
    if (view) view.focus();
    if (onFocused) onFocused();
  }, [focusSlotKey, slotIndex, showAll, onFocused]);
  const rows = [
    ...record.slots.map((slot) => {
      if (slot.invoice) {
        return <InvoiceRow key={slot.slotKey} slot={slot} onOpenInvoice={onOpenInvoice} onDeleteInvoice={onDeleteInvoice} disabled={disabled} />;
      }
      if (isNotApproved(slot)) return <NotApprovedRow key={slot.slotKey} slot={slot} />;
      if (isEmptyRecord(slot)) return <EmptyRecordRow key={slot.slotKey} slot={slot} />;
      return <RecordRow key={slot.slotKey} slot={slot} ticked={ticked} onToggle={onToggle} onView={onView} disabled={disabled} />;
    }),
    ...suggestions.map((g) => <SuggestionRow key={`${g.sourceKind}:${g.sourceRef}`} suggestion={g} onOpen={onOpenSuggestion} />),
  ];
  if (!rows.length) return null;
  const hidden = rows.length - ROW_CAP;
  return (
    <div className="border-t border-slate-300 pt-1 dark:border-slate-600">
      <ul ref={listRef} className="m-0 list-none p-0">{hidden > 0 && !showAll ? rows.slice(0, ROW_CAP) : rows}</ul>
      {hidden > 0 && (
        <button type="button" className={BTN_MORE} aria-expanded={showAll} onClick={() => setShowAll((open) => !open)}>
          {showAll ? ROW.showLess : ROW.showMore(hidden)}
        </button>
      )}
    </div>
  );
}
